import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { sendTradeClaimEmails } from "../../lib/TradeEmail";
import "../../styles/trading.css";
import { ClipLoader } from "react-spinners";


function Trading({ user }) {
  const [candles, setCandles] = useState([]);
  const [activeTrade, setActiveTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchActiveTrade = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setActiveTrade(userDoc.data().activeTrade || null);
          }
        } catch (err) {
          console.error("Error fetching active trade:", err);
        }
      }
      setLoading(false);
    };

    fetchActiveTrade();
  }, [user]);

  useEffect(() => {
    const initialCandles = Array.from({ length: 20 }, () => ({
      h: 8 + Math.random() * 15,
      l: 5 + Math.random() * 10,
      isUp: Math.random() > 0.5,
    }));
    setCandles(initialCandles);

    const interval = setInterval(() => {
      setCandles((prev) =>
        prev.map((candle) => ({
          h: Math.max(6, candle.h + (Math.random() - 0.5) * 6),
          l: Math.max(4, candle.l + (Math.random() - 0.5) * 4),
          isUp: Math.random() > 0.5,
        })),
      );
    }, 300);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <ClipLoader color="#D4292A" size={100} />
        <div className="center">
          Please wait, loading your account details...
        </div>
      </div>
    );
  }

  const investedAmount = activeTrade?.amount || 0;
  const expectedProfit = activeTrade?.earnings || 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isEndDateToday = () => {
    if (!activeTrade?.endDate) return false;
    const today = new Date();
    const endDate = new Date(activeTrade.endDate);
    return (
      today.getFullYear() === endDate.getFullYear() &&
      today.getMonth() === endDate.getMonth() &&
      today.getDate() === endDate.getDate()
    );
  };

  const claimAmount = activeTrade
    ? activeTrade.amount + activeTrade.earnings + activeTrade.profit
    : 0;

  const handleClaim = async () => {
    if (!user?.uid || !activeTrade || claiming) return;
    setClaiming(true);

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      const transaction = {
        account: "Investments",
        amount: claimAmount,
        beneficiary: userData.fullName || "User",
        beneficiaryAccount: "Sky FXT",
        date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
        description: `Investment plan ${activeTrade.name} completed with profit`,
        type: "credit",
      };

      await updateDoc(userDocRef, {
        role: "user",
        group: "cancelled",
        transactions: [...(userData.transactions || []), transaction],
        activeTrade: null,
      });

      setActiveTrade(null);

      await sendTradeClaimEmails(user.email, {
        userId: user.uid,
        userName: userData.fullName || "User",
        userEmail: user.email,
        planName: activeTrade.name,
        amount: claimAmount,
        date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
        transactionType: "Credit",
        status: "Completed",
      });

      navigate("/fxt/transactions");
    } catch (err) {
      console.error("Claim error:", err);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="trading-container">
      <div className="trading-card">
        <div>
          <div className="trading-header">
            <svg className="trading-header-icon" viewBox="0 0 24 24">
              <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" />
            </svg>
            <h2 className="trading-title">Trade in Progress</h2>
          </div>

          <div className="trading-dates">
            <div className="trading-date-item">
              <span className="trading-date-label">Started</span>
              <p className="trading-date-value">
                {formatDate(activeTrade?.createDate)}
              </p>
            </div>
            <div className="trading-date-item">
              <span className="trading-date-label">Ends</span>
              <p className="trading-date-value">
                {formatDate(activeTrade?.endDate)}
              </p>
            </div>
          </div>

          <div className="trading-chart">
            {candles.map((candle, i) => (
              <div key={i} className="trading-candle">
                <div
                  className={`trading-candle-body ${candle.isUp ? "" : "trading-candle-red"}`}
                  style={{ height: `${candle.h * 3}px` }}
                />
                <div
                  className={`trading-candle-wick ${candle.isUp ? "" : "trading-candle-red"}`}
                  style={{ height: `${candle.l * 3}px` }}
                />
              </div>
            ))}
          </div>

          <div className="trading-stats">
            <div className="trading-stat-item">
              <span className="trading-stat-label">Invested</span>
              <div className="trading-stat-value">
                <span className="trading-stat-currency">$</span>
                <span className="trading-stat-number">
                  {investedAmount.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="trading-stat-item">
              <span className="trading-stat-label">TPS Bonus</span>
              <div className="trading-stat-value">
                <span className="trading-stat-currency">+$</span>
                <span className="trading-stat-number trading-stat-number-green">
                  {expectedProfit.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="trading-stat-item">
              <span className="trading-stat-label">Expected Profit</span>
              <div className="trading-stat-value">
                <span className="trading-stat-currency">+$</span>
                <span className="trading-stat-number">
                  {activeTrade?.profit || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="trading-card trading-card-light trading-plan-details">
        <div>
          <h2 className="trading-title">Plan Details</h2>

          <p className="trading-plans-hardware">
            {activeTrade?.hardware || "No hardware info available"}
          </p>

          <h2 className="trading-plans-specs-title">Specs</h2>
          <div className="trading-specs-list">
            {(activeTrade?.specs || []).map((spec, i) => (
              <div key={i} className="trading-spec-item">
                <div className="trading-spec-dot" />
                <span className="trading-specs-text">{spec}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`trading-status ${isEndDateToday() ? "trading-status-claim" : "trading-status-active"} ${claiming ? "trading-status-claiming" : ""}`}
          onClick={isEndDateToday() && !claiming ? handleClaim : undefined}
        >
          <div className="trading-status-left">
            <div className="trading-status-dot" />
            <span className="trading-status-text">
              {claiming ? "Processing..." : activeTrade ? `Active: ${activeTrade.name}` : "No active trade"}
            </span>
          </div>
          {isEndDateToday() && (
            <span className="trading-status-action">
              Claim ${claimAmount.toLocaleString()} →
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Trading;
